import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Trans } from '../i18n';

const Input = ({
	label,
	type = 'text',
	placeholder,
	icon,
	errorText,
	validation,
	validationFn,
	onChange,
	autoComplete = 'off',
	...props
}) => {
	const [showPassword, setShowPassword] = useState(false);
	const [valid, setValid] = useState(true);
	const ref = useRef(null);

	const handleTogglePassword = () => {
		setShowPassword(!showPassword);
	};

	// If it's a password field and showPassword is true, switch the type to text
	const inputType = type === 'password' && showPassword ? 'text' : type;

	// Validate input if one is provided, fire onBlur event
	const handleBlur = () => {
		if (validation) {
			setValid(validation.test(ref.current.value));
		} else if (validationFn) {
			setValid(validationFn(ref.current.value));
		}
	};

	const handleChange = (e) => {
		if (!valid) {
			if (validation) {
				setValid(validation.test(e.target.value));
			} else if (validationFn) {
				setValid(validationFn(e.target.value));
			}
		}

		if (onChange) {
			onChange(e);
		}
	};

	return (
		<label className='block w-full'>
			{label && <span className='mb-1 block text-sm font-medium text-gray-700'>{label}</span>}
			<div className='relative'>
				{/* Left Icon (if provided) */}
				{icon && (
					<i
						className={`${icon} absolute left-3 top-1/2 -translate-y-1/2 ${
							valid ? 'text-primary-text' : 'text-on-error'
						}`}
					/>
				)}

				{/* Input field */}
				<input
					ref={ref}
					type={inputType}
					placeholder={placeholder}
					className={`
						w-full rounded-md border text-md  ${
							valid
								? 'bg-white text-primary-text border-default'
								: 'bg-error text-error-text border-on-error'
						} py-2 px-3
					  placeholder-gray-400 focus:outline-none
						focus:ring-1 focus:ring-border-active focus:border-border-active
						disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200
						${icon ? 'pl-10' : ''}
						${type === 'password' ? 'pr-10' : ''}
					`}
					{...props}
					onBlur={handleBlur}
					onChange={handleChange}
					autoComplete={autoComplete}
				/>

				{/* Right toggle icon if it's a password input */}
				{type === 'password' && (
					<button
						type='button'
						onClick={handleTogglePassword}
						className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400
                       hover:text-gray-600 focus:outline-none focus-visible:ring-1 focus-visible:ring-border-active focus-visible:rounded-sm'
					>
						<span className='flex items-center gap-2 text-sm font-medium '>
							{showPassword ? (
								<>
									<Trans i18nKey='login.hide_password' className='underline' />
									<i className='ri-eye-off-line' />
								</>
							) : (
								<>
									<Trans i18nKey='login.show_password' className='underline' />
									<i className='ri-eye-line focus-visible:ring-1 focus-visible:ring-offset-0' />
								</>
							)}
						</span>
					</button>
				)}
			</div>
			{/* Error message container with fixed height */}
			{(validation || validationFn) && (
				<div className='min-h-14'>
					{errorText && !valid && (
						<span className='text-error-text text-sm'>
							<i className='ri-error-warning-fill text-on-error mr-1' />
							{errorText}
						</span>
					)}
				</div>
			)}
		</label>
	);
};

Input.propTypes = {
	label: PropTypes.string,
	type: PropTypes.string,
	placeholder: PropTypes.string,
	icon: PropTypes.string,
	errorText: PropTypes.string,
	valid: PropTypes.bool,
	validation: PropTypes.shape(RegExp),
	validationFn: PropTypes.func,
	onChange: PropTypes.func,
	autoComplete: PropTypes.string,
};

export default Input;
